import _ from 'lodash';
import { getIntl } from 'umi';

import { bindServices } from '@/services/api';
import {
  toPaginate,
  getFiltersGlobalFilters,
  getBulkImportErrorData,
} from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

import { queryAddUserGroupUser, queryUserGroupUsers } from './service';

const api = bindServices('userGroups');
const { BulkActionType } = CoreEnums;

const namespace = 'userGroups';
const fetchRoute = 'userGroup';
const fetchParams = 'userGroup';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams].paged.select,
  showForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  showImport: false,
  bulkActionErrorData: null,
  showBulkActionErrors: false,
  // group-users drawer (usuarios del grupo)
  newUserOnGroup: false,
  groupUsersItem: null,
  formConfigGroupUsers: null,
  listUserGroupUser: [],
};

const UserGroupsModel = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams].paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        const items = res?.items || res?.data || [];
        const page = payload.page ?? 1;
        const limit = payload.limit ?? pageSize;
        const totalCount = res?.totalCount ?? res?.count ?? res?.total ?? items.length;
        yield put({
          type: 'queryData',
          payload: {
            ...res,
            limit,
            page,
            totalCount,
            items,
          },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        const item = Array.isArray(res) ? res[0] : res;
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: fetchRoute, data: payload.data });
        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({
          type: 'global/modalConfirm',
          payload: { state: false, id: null },
        });
        yield put({
          type: 'queryShowForm',
          payload: {
            state: false,
            item: null,
            detail: false,
            id: null,
          },
        });
        yield call(api.remove, { route: fetchRoute, id });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: payload.params || {} });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { call, put }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'userGroupActivate', payload });
      } catch (e) {
        /*  */
      }
    },

    *bulkDeleteUserGroups({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        for (const id of ids) {
          yield call(api.remove, { route: fetchRoute, id });
        }

        const pageSize = yield select((state) => state.global.pageSize);
        const state = yield select((s) => s.userGroups);
        const globalState = yield select((s) => s.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(state.orderBy && { orderBy: state.orderBy }),
          ...(filters && { filter: filters }),
          select: state.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateUserGroups({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkUserGroupActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeactivateUserGroups({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkUserGroupActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *addNewUserOnGroup({ payload }, { put }) {
      yield put({ type: 'addUserOnGroup', payload });
    },

    *fetchUserOnGroup({ payload }, { call, put }) {
      try {
        const response = yield call(queryUserGroupUsers, payload);
        yield put({ type: 'listUserOnGroup', response });
      } catch (error) {
        /*  */
      }
    },

    *hideNewUserOnGroup({ payload = 'hideUserOnGroup' }, { put }) {
      yield put({ type: payload });
    },

    *onCloseUserOnGroup(_, { put }) {
      yield put({ type: 'closeUserOnGroup' });
    },

    *addUserGroupUser({ payload }, { put }) {
      yield put({ type: 'addUserGroupList', payload });
    },

    *saveUserGroupUsers({ payload }, { call, put }) {
      try {
        yield call(queryAddUserGroupUser, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'addUserOnGroup',
          payload: { state: false, item: null, form: null },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: false, visibleForm: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *deleteItemUserGroup({ id }, { put }) {
      try {
        yield put({ type: 'deleteUserGroupUser', payload: id });
        yield put({
          type: 'global/modalConfirmDrawer',
          payload: { state: false, id: null },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *setPaginationPage({ payload }, { put }) {
      yield put({ type: 'queryPaginationPage', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },

    *toggleImportComponent({ payload }, { put }) {
      yield put({
        type: 'queryToggleImportComponent',
        payload: { showImport: payload.show },
      });
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const { file, isDeleteData } = payload;
        const pageSize = yield select((state) => state.global.pageSize);
        const stateModel = yield select((state) => state.userGroups);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams].paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateModel.orderBy && { orderBy: stateModel.orderBy }),
          ...(filters && { filter: filters }),
        };

        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import`,
          method: 'post',
          data: { fileContent: file, deleteExistingData: isDeleteData },
        });

        const errorData = getBulkImportErrorData(res, getIntl);
        const hasImportedRows = res.importedRowsCount && res.importedRowsCount > 0;

        if (errorData) {
          yield put({
            type: 'setBulkActionErrors',
            payload: { errorData },
          });
          yield put({
            type: 'toggleBulkActionErrorsDrawer',
            payload: { open: true },
          });
        }

        yield put({ type: 'queryImport', payload: { ...res } });

        if (hasImportedRows) {
          yield put({ type: 'fetchList', payload: params });
        }
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *toggleBulkActionErrorsDrawer({ payload }, { put }) {
      yield put({
        type: 'queryToggleBulkActionErrorsDrawer',
        payload,
      });
    },

    *clearBulkActionErrors(_, { put }) {
      yield put({ type: 'queryClearBulkActionErrors' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      const items = payload.items ?? payload.data ?? state.list;
      return {
        ...state,
        list: items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    userGroupActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    bulkUserGroupActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: payload,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    editOrDetail(state, { payload }) {
      return {
        ...state,
        item: payload.item ?? {},
      };
    },

    queryOrderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    queryPaginationPage(state, { payload }) {
      return {
        ...state,
        pagination: { ...state.pagination, page: payload },
      };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    queryToggleImportComponent(state, { payload }) {
      return {
        ...state,
        showImport: payload.showImport ?? payload.show,
      };
    },

    queryImport(state) {
      return {
        ...state,
        showImport: false,
      };
    },

    setBulkActionErrors(state, { payload }) {
      return {
        ...state,
        bulkActionErrorData: payload.errorData,
        showBulkActionErrors: true,
      };
    },

    queryToggleBulkActionErrorsDrawer(state, { payload }) {
      return {
        ...state,
        showBulkActionErrors: payload.open,
      };
    },

    queryClearBulkActionErrors(state) {
      return {
        ...state,
        bulkActionErrorData: null,
        showBulkActionErrors: false,
      };
    },

    addUserOnGroup(state, { payload }) {
      return {
        ...state,
        newUserOnGroup: payload.state,
        groupUsersItem: payload.item,
        formConfigGroupUsers: payload.form,
      };
    },

    hideUserOnGroup(state) {
      return {
        ...state,
        groupUsersItem: null,
        listUserGroupUser: [],
        formConfigGroupUsers: null,
      };
    },

    closeUserOnGroup(state) {
      return { ...state, newUserOnGroup: false };
    },

    listUserOnGroup(state, { response }) {
      return {
        ...state,
        listUserGroupUser: (response || []).map((item) => ({
          id: item.id,
          user: item.fullName,
        })),
      };
    },

    addUserGroupList(state, { payload }) {
      return {
        ...state,
        listUserGroupUser: [...state.listUserGroupUser, payload],
      };
    },

    deleteUserGroupUser(state, { payload }) {
      return {
        ...state,
        listUserGroupUser: state.listUserGroupUser.filter((item) => item.id !== payload),
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default UserGroupsModel;
